import { appState } from "./state";
import { getSharedState } from "./windowBus";

// Framework-agnostic request queue backing pickAlbum() (albumPicker.ts).
// Same split as toastBus.ts/dialogBus.ts, entirely windowBus-backed for
// the same cross-bundle reason (see windowBus.ts). appState itself is
// already windowBus-backed (state.ts), so reading it here is safe
// regardless of which bundle requestAlbumPick() is called from.
export interface PickedAlbum {
  id: string;
  name: string;
}

interface PickerRequest {
  id: number;
  title: string;
  targets: PickedAlbum[];
  resolve: (v: PickedAlbum | null) => void;
}

interface PickerBusState {
  current: PickerRequest | null;
  nextId: number;
  listeners: Set<() => void>;
}

function state(): PickerBusState {
  return getSharedState<PickerBusState>("__albumPickerBusState", () => ({
    current: null,
    nextId: 1,
    listeners: new Set(),
  }));
}

function notify(): void {
  state().listeners.forEach((fn) => fn());
}

export function subscribePicker(fn: () => void): () => void {
  state().listeners.add(fn);
  return () => state().listeners.delete(fn);
}

export function getCurrentPicker(): PickerRequest | null {
  return state().current;
}

// Targets are snapshotted from appState.albums at request time, matching
// the original's behavior of rebuilding the list fresh on each open
// rather than live-updating while the picker is shown.
export function requestAlbumPick(title: string, opts: { includeAll?: boolean } = {}): Promise<PickedAlbum | null> {
  const includeAll = opts.includeAll !== false;
  const source = includeAll ? appState.albums : appState.albums.filter((a) => a.id !== "all");
  const targets = source.map((a) => ({ id: a.id, name: a.name }));

  return new Promise((resolve) => {
    const s = state();
    s.current = { id: s.nextId++, title, targets, resolve };
    notify();
  });
}

export function resolveAlbumPick(value: PickedAlbum | null): void {
  const s = state();
  if (!s.current) return;
  s.current.resolve(value);
  s.current = null;
  notify();
}
